import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Trash2, Gem, X } from 'lucide-react';
import api from '../services/api';

// ─── A simple "Name list" sub-tab for Family / Origin / Form ────────────────
function NameList({ endpoint, label }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      setItems(r.data.items);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post(endpoint, { name });
      setNewName('');
      toast.success(`${label} added`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      setConfirmId(null);
      toast.success(`${label} removed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); setConfirmId(null); }
  };

  return (
    <div className="space-y-2" data-testid={`namelist-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={`Add a ${label}…`}
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
          data-testid="name-input"
        />
        <button
          onClick={add}
          className="px-3 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
          disabled={!newName.trim()}
          data-testid="name-add"
        >Add</button>
      </div>
      {loading ? <div className="text-center py-6 text-gray-400 text-sm">Loading…</div>
        : items.length === 0 ? <div className="text-center py-6 text-gray-400 text-sm">No {label.toLowerCase()} entries yet.</div>
        : (
          <div className="space-y-1">
            {items.map(it => (
              <div key={it.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-3 py-2" data-testid={`name-row-${it.id}`}>
                <span className="text-sm font-medium text-gray-800">{it.name}</span>
                {confirmId === it.id ? (
                  <div className="flex gap-1">
                    <button onClick={() => remove(it.id)} className="px-2 py-1 rounded-md bg-red-600 text-white text-[10px] font-bold">Delete</button>
                    <button onClick={() => setConfirmId(null)} className="px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-[10px] font-bold">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmId(it.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-${it.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ─── Stones sub-tab (Name + Variant) ─────────────────────────────────────────
function StoneList() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName]     = useState('');
  const [variant, setVariant] = useState('');
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/gemstone-master/stones'); setItems(r.data.items); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post('/gemstone-master/stones', { stone_name: name.trim(), variant: variant.trim() });
      setName(''); setVariant('');
      toast.success('Stone added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };
  const remove = async (id) => {
    try { await api.delete(`/gemstone-master/stones/${id}`); setConfirmId(null); toast.success('Stone removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); setConfirmId(null); }
  };

  return (
    <div className="space-y-2" data-testid="namelist-stones">
      <div className="grid grid-cols-5 gap-2">
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Stone name (required)"
          className="col-span-2 border rounded-lg px-3 py-2 text-sm" data-testid="stone-name-input" />
        <input type="text" value={variant} onChange={e => setVariant(e.target.value)} placeholder="Variant (optional)"
          className="col-span-2 border rounded-lg px-3 py-2 text-sm" data-testid="stone-variant-input" />
        <button onClick={add} disabled={!name.trim()}
          className="rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
          data-testid="stone-add">Add</button>
      </div>
      {loading ? <div className="text-center py-6 text-gray-400 text-sm">Loading…</div>
        : (
          <div className="space-y-1">
            <p className="text-[10px] text-gray-500">{items.length} stones</p>
            {items.map(it => (
              <div key={it.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-3 py-2" data-testid={`stone-row-${it.id}`}>
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800">{it.stone_name}</span>
                  {it.variant && <span className="ml-2 text-xs text-gray-500">· {it.variant}</span>}
                </div>
                {confirmId === it.id ? (
                  <div className="flex gap-1">
                    <button onClick={() => remove(it.id)} className="px-2 py-1 rounded-md bg-red-600 text-white text-[10px] font-bold">Delete</button>
                    <button onClick={() => setConfirmId(null)} className="px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-[10px] font-bold">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmId(it.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ─── Combinations tab ────────────────────────────────────────────────────────
function CombinationsTab() {
  const [combos, setCombos] = useState([]);
  const [families, setFamilies] = useState([]);
  const [origins, setOrigins]   = useState([]);
  const [forms, setForms]       = useState([]);
  const [stones, setStones]     = useState([]);
  const [rules, setRules]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [search, setSearch]     = useState('');
  const [familyF, setFamilyF]   = useState('');
  const [formFilters, setFormFilters] = useState([]);   // multi-select pill row
  const [confirmId, setConfirmId] = useState(null);

  const [form, setForm] = useState({ family: '', origin: '', form: '', stone_name: '', regular_price: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, fams, ors, fms, st, rs] = await Promise.all([
        api.get('/gemstone-master/combinations', { params: search ? { q: search, family: familyF || undefined } : { family: familyF || undefined } }),
        api.get('/gemstone-master/families'),
        api.get('/gemstone-master/origins'),
        api.get('/gemstone-master/forms'),
        api.get('/gemstone-master/stones'),
        api.get('/gemstone-master/family-rules'),
      ]);
      setCombos(c.data.items); setFamilies(fams.data.items); setOrigins(ors.data.items);
      setForms(fms.data.items); setStones(st.data.items); setRules(rs.data.items);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [search, familyF]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Family-restricted dropdowns
  const familyRule = useMemo(() => rules.find(r => r.family === form.family) || null, [rules, form.family]);
  const allowedOrigins = familyRule ? familyRule.allowed_origins : [];
  const allowedForms   = familyRule ? familyRule.allowed_forms   : [];

  // Form pills source — Family-aware if a Family filter is active, else all gem_forms
  const filterFamilyRule = useMemo(
    () => rules.find(r => r.family === familyF) || null,
    [rules, familyF]
  );
  const availableFormPills = filterFamilyRule
    ? filterFamilyRule.allowed_forms
    : forms.map(f => f.name);

  // If Family filter changes, drop any form pills no longer valid
  useEffect(() => {
    if (filterFamilyRule) {
      setFormFilters(prev => prev.filter(f => filterFamilyRule.allowed_forms.includes(f)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyF]);

  const toggleFormPill = (f) => {
    setFormFilters(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  };

  // Client-side filter: apply selected form pills
  const visibleCombos = useMemo(() => {
    if (formFilters.length === 0) return combos;
    return combos.filter(c => formFilters.includes(c.form));
  }, [combos, formFilters]);

  const onFamilyChange = (next) => {
    const rule = rules.find(r => r.family === next);
    setForm(f => ({
      ...f, family: next,
      origin: rule?.allowed_origins?.includes(f.origin) ? f.origin : '',
      form:   rule?.allowed_forms?.includes(f.form)     ? f.form   : '',
    }));
  };

  const save = async () => {
    try {
      await api.post('/gemstone-master/combinations', {
        family: form.family, origin: form.origin, form: form.form,
        stone_name: form.stone_name,
        regular_price: form.regular_price === '' ? null : Number(form.regular_price),
      });
      toast.success('Combination added');
      setShowAdd(false);
      setForm({ family: '', origin: '', form: '', stone_name: '', regular_price: '' });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/gemstone-master/combinations/${id}?hard=true`); setConfirmId(null); toast.success('Removed'); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); setConfirmId(null); }
  };

  return (
    <div className="space-y-3" data-testid="combinations-tab">
      <div className="flex gap-2 items-center">
        <input type="text" placeholder="Search stone name…" value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm bg-white" data-testid="combo-search" />
        <select value={familyF} onChange={e => setFamilyF(e.target.value)}
          className="border rounded-lg px-2 py-2 text-xs bg-white" data-testid="combo-filter-family">
          <option value="">All Families</option>
          {families.map(f => <option key={f.id}>{f.name}</option>)}
        </select>
        <button onClick={() => setShowAdd(true)} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold flex items-center gap-1" data-testid="combo-add">
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      <p className="text-[11px] text-gray-500">{visibleCombos.length} of {combos.length} combinations</p>
      {/* Form-filter pill row (multi-select) */}
      {availableFormPills.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="form-filter-row">
          <span className="text-[10px] font-semibold uppercase text-gray-500 mr-1">Form:</span>
          {availableFormPills.map(f => {
            const active = formFilters.includes(f);
            return (
              <button
                key={f}
                onClick={() => toggleFormPill(f)}
                data-testid={`form-pill-${f}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                }`}
              >
                {f}
              </button>
            );
          })}
          {formFilters.length > 0 && (
            <button
              onClick={() => setFormFilters([])}
              className="ml-1 text-[10px] font-bold text-rose-600 underline"
              data-testid="form-pill-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}
      {loading ? <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
        : visibleCombos.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm">No combinations match.</div>
        : (
          <div className="space-y-1.5">
            {visibleCombos.map(c => (
              <div key={c.id} className="bg-white border border-gray-100 rounded-lg px-3 py-2 flex items-center gap-3" data-testid={`combo-row-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800 truncate">{c.stone_name}</p>
                  <p className="text-[11px] text-gray-500">{c.family} · {c.origin} · {c.form}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-700">{c.regular_price == null ? '—' : `₹${c.regular_price.toLocaleString()}`}</p>
                  <p className="text-[10px] text-gray-400">Regular</p>
                </div>
                {confirmId === c.id ? (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => remove(c.id)} className="px-2 py-1 rounded-md bg-red-600 text-white text-[10px] font-bold">Delete</button>
                    <button onClick={() => setConfirmId(null)} className="px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-[10px] font-bold">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmId(c.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`combo-delete-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" data-testid="combo-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">New Combination</h3>
              <button onClick={() => setShowAdd(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Family"><select value={form.family} onChange={e => onFamilyChange(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="combo-family">
                <option value="">— pick —</option>
                {families.map(f => <option key={f.id}>{f.name}</option>)}
              </select></Field>
              <Field label={`Origin ${allowedOrigins.length ? `(${allowedOrigins.length} allowed)` : ''}`}><select value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white" disabled={!allowedOrigins.length} data-testid="combo-origin">
                <option value="">— pick —</option>
                {allowedOrigins.map(n => <option key={n}>{n}</option>)}
              </select></Field>
              <Field label={`Form ${allowedForms.length ? `(${allowedForms.length} allowed)` : ''}`}><select value={form.form} onChange={e => setForm({ ...form, form: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white" disabled={!allowedForms.length} data-testid="combo-form">
                <option value="">— pick —</option>
                {allowedForms.map(n => <option key={n}>{n}</option>)}
              </select></Field>
              <Field label="Stone Name"><select value={form.stone_name} onChange={e => setForm({ ...form, stone_name: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="combo-stone">
                <option value="">— pick —</option>
                {stones.map(s => (
                  <option key={s.id} value={s.stone_name}>
                    {(s.variant || '').trim() ? `${s.stone_name} — ${s.variant}` : s.stone_name}
                  </option>
                ))}
              </select></Field>
              <Field label="Regular Price (₹)"><input type="number" step="0.01" value={form.regular_price}
                onChange={e => setForm({ ...form, regular_price: e.target.value })}
                placeholder="e.g. 3500" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="combo-price" /></Field>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-700">Cancel</button>
              <button onClick={save} disabled={!form.family || !form.origin || !form.form || !form.stone_name}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-50" data-testid="combo-save">
                Add Combination
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

// ─── Family Rules sub-tab ────────────────────────────────────────────────────
function FamilyRulesTab() {
  const [rules, setRules] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [forms, setForms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingFamily, setSavingFamily] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o, f] = await Promise.all([
        api.get('/gemstone-master/family-rules'),
        api.get('/gemstone-master/origins'),
        api.get('/gemstone-master/forms'),
      ]);
      setRules(r.data.items);
      setOrigins(o.data.items.map(x => x.name));
      setForms(f.data.items.map(x => x.name));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (family, list, value, key) => {
    const rule = rules.find(r => r.family === family);
    if (!rule) return;
    const next = list.includes(value)
      ? list.filter(x => x !== value)
      : [...list, value];
    saveRule(family, { [key]: next });
  };
  const saveRule = async (family, patch) => {
    setSavingFamily(family);
    try {
      await api.patch(`/gemstone-master/family-rules/${encodeURIComponent(family)}`, patch);
      setRules(prev => prev.map(r => r.family === family ? { ...r, ...patch } : r));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); load(); }
    finally { setSavingFamily(''); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>;
  if (rules.length === 0) return <div className="text-center py-12 text-gray-400 text-sm">No families yet. Add one under the Family sub-tab.</div>;

  return (
    <div className="space-y-3" data-testid="family-rules-tab">
      <p className="text-[11px] text-gray-500">
        Tick which Origins and Forms each Family allows. Used to filter dropdowns in the Combinations tab.
      </p>
      {rules.map(rule => (
        <div key={rule.id} className="bg-white border border-gray-100 rounded-xl p-3 space-y-2.5" data-testid={`rule-card-${rule.family}`}>
          <p className="text-sm font-bold text-gray-800">{rule.family} {savingFamily === rule.family && <span className="text-[10px] text-emerald-600 ml-1">saving…</span>}</p>
          <div>
            <p className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Allowed Origins</p>
            <div className="flex flex-wrap gap-1.5">
              {origins.map(o => {
                const active = rule.allowed_origins?.includes(o);
                return (
                  <button key={o} onClick={() => toggle(rule.family, rule.allowed_origins || [], o, 'allowed_origins')}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}
                    data-testid={`rule-${rule.family}-origin-${o}`}>
                    {o}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Allowed Forms</p>
            <div className="flex flex-wrap gap-1.5">
              {forms.map(f => {
                const active = rule.allowed_forms?.includes(f);
                return (
                  <button key={f} onClick={() => toggle(rule.family, rule.allowed_forms || [], f, 'allowed_forms')}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}
                    data-testid={`rule-${rule.family}-form-${f}`}>
                    {f}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function GemstoneMaster() {
  const navigate = useNavigate();
  const [tab, setTab]       = useState('names');
  const [subTab, setSubTab] = useState('family');

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/40 via-rose-50/30 to-white">
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="px-3 py-2.5 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-gray-50" data-testid="gm-back"><ChevronLeft className="w-5 h-5" /></button>
          <div className="flex items-center gap-2">
            <Gem className="w-4 h-4 text-amber-600" />
            <h1 className="text-sm font-bold text-gray-800">Gemstone Master</h1>
          </div>
          <div className="w-9" />
        </div>
        <div className="flex border-t border-gray-100">
          {['names', 'combinations'].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`flex-1 py-2.5 text-sm font-bold ${tab === t ? 'text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/40' : 'text-gray-500'}`}>
              {t === 'names' ? 'Names' : 'Combinations'}
            </button>
          ))}
        </div>
        {tab === 'names' && (
          <div className="flex gap-1 px-3 py-2 overflow-x-auto border-t border-gray-100">
            {['family', 'origin', 'form', 'stones', 'rules'].map(s => (
              <button key={s} onClick={() => setSubTab(s)} data-testid={`subtab-${s}`}
                className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${subTab === s ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                {s === 'stones' ? 'Stone Name + Variant' : s === 'rules' ? 'Family Rules' : s[0].toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-3 py-4 max-w-4xl mx-auto">
        {tab === 'names' && subTab === 'family' && <NameList endpoint="/gemstone-master/families" label="Family" />}
        {tab === 'names' && subTab === 'origin' && <NameList endpoint="/gemstone-master/origins"  label="Origin" />}
        {tab === 'names' && subTab === 'form'   && <NameList endpoint="/gemstone-master/forms"    label="Form" />}
        {tab === 'names' && subTab === 'stones' && <StoneList />}
        {tab === 'names' && subTab === 'rules'  && <FamilyRulesTab />}
        {tab === 'combinations' && <CombinationsTab />}
      </div>
    </div>
  );
}
